import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import * as authApi from '../auth.api';
import { activeLanguages } from '@/i18n/config';

interface LanguageSelectorProps {
  /** When true, saves preference to API (use after login) */
  persistToApi?: boolean;
  /** Lets a caller (e.g. a <label htmlFor>) associate an accessible name (NFR9). */
  id?: string;
}

export function LanguageSelector({ persistToApi = false, id }: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const handleChange = (newLang: string) => {
    i18n.changeLanguage(newLang);

    // Persist to API if authenticated and requested
    if (persistToApi && isAuthenticated) {
      authApi.updatePreferences({ language: newLang }).catch(() => {
        // Fire-and-forget — localStorage already persists via i18next
      });
    }
  };

  return (
    <select
      id={id}
      data-testid="language-selector"
      value={i18n.language}
      onChange={(e) => handleChange(e.target.value)}
      className="cursor-pointer rounded-lg px-3 py-1.5 pr-7 text-xs outline-none"
      style={{
        appearance: 'none',
        WebkitAppearance: 'none',
        backgroundColor: 'var(--background)',
        border: '1px solid var(--border)',
        color: 'var(--foreground-muted)',
        fontFamily: 'inherit',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 5l3 3 3-3' fill='none' stroke='%2364748B' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 10px center',
      }}
    >
      {activeLanguages.map(({ code, nativeName }) => (
        <option key={code} value={code}>
          {code.toUpperCase()} — {nativeName}
        </option>
      ))}
    </select>
  );
}
